import AntdColorPicker                              from 'antd/lib/color-picker';
import React                                        from 'react';
import { Color as AntdColor }                       from 'antd/lib/color-picker';
import { ColorPickerProps as AntdColorPickerProps } from 'antd/lib/color-picker';
import { checkIsInvisible }                         from '@widesk-core/tools/colors';
import { TooltipPlacement }                         from 'antd/lib/tooltip';

type Color = AntdColor | string;

export type ColorPickerProps = Omit<FieldProps, 'onChange'> & {
	hidePreset?: true;
	disabledAlpha?: boolean;
	showText?: boolean | ((color: Color) => React.ReactNode);
	value?: Color,
	onChange?: (value?: string) => void;
	onChangeComplete?: (value?: string) => void;
	allowClear?: boolean;
	children?: React.ReactNode;
	panelRender?: AntdColorPickerProps['panelRender'];
	onOpenChange?: (open: boolean) => void;
	presets?: { label: string, colors: string[] }[];
	/**
	 * Côté d'ouverture du panneau. Le repli automatique d'antd choisit entre le HAUT et le BAS : dans
	 * un conteneur étroit et haut, les deux peuvent manquer et le panneau sort de l'écran. Ouvrir sur
	 * le côté (`rightTop`) règle le cas. Défaut antd : `bottomLeft`.
	 */
	placement?: TooltipPlacement;
	getPopupContainer?: (node: HTMLElement) => HTMLElement;
	autoAdjustOverflow?: boolean;
}

const pastelRainbowPalette = {
	label: 'Palette mixte',
	colors: [
		'#9372ad', '#d481b2', '#ea91a3', '#efad85',
		'#f3c777', '#f4ed99', '#b8d590', '#8fccb9',
		'#7ccada', '#82b1d8',
	],
};

// Retire le canal alpha d'une couleur hexa (#RRGGBBAA -> #RRGGBB, #RGBA -> #RGB)
// nécessaire car antd log un warning si `disabledAlpha` est actif alors que la
// couleur fournie contient encore un canal alpha non opaque.
function forceOpaqueColor(color: string): string {
	if (color.startsWith('#') && color.length === 9) return color.slice(0, 7);
	if (color.startsWith('#') && color.length === 5) return color.slice(0, 4);

	return color;
}

export default function ColorPicker({
	value: propsValue,
	disabledAlpha = true,
	showText = true,
	...props
}: ColorPickerProps) {
	const countValueRef = React.useRef(0);

	const rawValue = propsValue || (disabledAlpha ? propsValue : '');
	const value = disabledAlpha && typeof rawValue === 'string' ? forceOpaqueColor(rawValue) : rawValue;

	// Tant qu'aucune couleur n'est choisie, antd considère en interne la couleur comme
	// "effacée" avec une alpha à 0, ce qui déclenche le warning `disabledAlpha` même en
	// l'absence de toute couleur avec alpha. On lui donne une couleur opaque de secours
	// uniquement pour son état interne : l'affichage ("Choisir une couleur") reste basé sur `value`.
	const antdValue = disabledAlpha && !value ? '#000000' : value;

	const manageChangedColor = (color: string) => {
		// Si la couleur est en opacity 0 on la repasse à 1 dès qu'elle est modifiée
		if (!disabledAlpha && checkIsInvisible(value?.toString() || '') && checkIsInvisible(color)) {
			return color.slice(0, -2) + 'fe';
		}

		return color;
	};

	const antdShowText = showText === false
		? false
		: () => value ? `${value.toString().toUpperCase()}` : 'Choisir une couleur';

	return (
		<AntdColorPicker
			disabledAlpha={disabledAlpha}
			disabledFormat
			format="hex"
			presets={props.hidePreset ? undefined : [pastelRainbowPalette]}
			showText={antdShowText}
			value={antdValue}

			{...props}

			onClear={() => props.onChange?.(undefined)}
			style={{ justifyContent: 'unset', ...props.style }}
			onChange={color => {
				if (color.cleared) {
					props.onChange?.(undefined);
					return undefined;
				}

				const customColor = manageChangedColor(color.toHexString());
				props.onChange?.(customColor);
				countValueRef.current++;

				return customColor;
			}}
			onChangeComplete={color => {
				if (color.cleared) {
					props.onChangeComplete?.(undefined);
					return;
				}

				const customColor = manageChangedColor(color.toHexString());
				props.onChangeComplete?.(customColor);
			}}
		/>
	);
}